import { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { searchPostalCodes } from 'src/utils/postalCodes';

// Her tuşta istek atmamak için bekleme süresi
const DEBOUNCE_MS = 300;

const getOptionText = (option) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option.postalCode;
};

/**
 * Posta kodu alanı. Kullanıcı yazdıkça seçili ülkenin posta kodlarını servisten
 * önekle sorgular, seçim yapılınca onChange ile hem kodu hem şehri verir.
 *
 * Servis hazır değilken veya sonuç dönmediğinde serbest metin girişi olarak
 * çalışır; kullanıcı istediği kodu elle yazabilir.
 */
export default function PostalCodeAutocomplete({
  label = 'Posta Kodu',
  countryCode,
  value,
  onChange,
  error,
  helperText,
  ...other
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Geç dönen isteklerin güncel sonucu ezmesini engeller.
  const requestIdRef = useRef(0);

  // Dışarıdan gelen değer değişirse (form sıfırlama gibi) input'u eşitle.
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (!countryCode || !inputValue.trim()) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    const timer = setTimeout(() => {
      searchPostalCodes({ countryCode, prefix: inputValue }).then((list) => {
        // Sadece en son istek sonucu uygulanır.
        if (requestIdRef.current !== requestId) return;
        setOptions(list);
        setLoading(false);
      });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [countryCode, inputValue]);

  const noOptionsText = () => {
    if (!countryCode) return 'Önce ülke seçin';
    return 'Eşleşen posta kodu yok';
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      options={options}
      filterOptions={(list) => list}
      loading={loading}
      loadingText="Aranıyor…"
      inputValue={inputValue}
      onInputChange={(_event, next, reason) => {
        setInputValue(next);
        // Seçim yapıldığında onChange zaten aşağıda tetikleniyor.
        if (reason === 'input') {
          onChange({ postalCode: next, city: '' });
        }
      }}
      onChange={(_event, option) => {
        if (!option) {
          onChange({ postalCode: '', city: '' });
          return;
        }
        if (typeof option === 'string') {
          onChange({ postalCode: option, city: '' });
          return;
        }
        onChange({ postalCode: option.postalCode, city: option.city });
      }}
      getOptionLabel={getOptionText}
      isOptionEqualToValue={(option, selected) => option.postalCode === selected?.postalCode}
      renderOption={(props, option) => (
        <li {...props} key={option.postalCode}>
          <span style={{ fontWeight: 600, marginRight: 8 }}>{option.postalCode}</span>
          <span style={{ color: '#6B7280' }}>
            {[option.city, option.region].filter(Boolean).join(', ')}
          </span>
        </li>
      )}
      noOptionsText={noOptionsText()}
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...other}
    />
  );
}
